import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { V, useVocab } from "@/lib/vocab";
import { toast } from "sonner";
import { useAudiences } from "@/lib/audiences";
import { UserPlus, Copy, Trash2, ShieldCheck, Users2, Link2, Building2, Check, Settings2, Plus, BellRing, Send, Mail, Phone, Home, Search, Unlink, Clock, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import { AdminPhoneVerify } from "@/components/AdminPhoneVerify";
import { ExtraNumbers, adminPhoneEndpoints } from "@/components/ExtraNumbers";
import { ReachabilityBadge } from "@/components/ReachabilityBadge";
import { InviteMatchPanel } from "@/components/InviteMatchPanel";

const roleMetaFactory = () => ({
  super_admin: { label: "Super Admin", color: "bg-redline-50 text-redline-700" },
  platform_admin: { label: "Platform Admin", color: "bg-amber-50 text-amber-700" },
  onboarding_admin: { label: "Onboarding", color: "bg-lime-50 text-lime-700" },
  operator: { label: "Operator", color: "bg-teal-50 text-teal-700" },
  company_admin: { label: V.role("company_admin"), color: "bg-indigo-50 text-indigo-700" },
  regional_manager: { label: V.role("regional_manager"), color: "bg-cyan-50 text-cyan-700" },
  hoa_manager: { label: V.role("hoa_manager"), color: "bg-blue-50 text-blue-700" },
  board_member: { label: V.role("board_member"), color: "bg-violet-50 text-violet-700" },
});

const COMMUNITY_ROLES = ["regional_manager", "hoa_manager", "board_member"];
const needsCommunities = (r) => COMMUNITY_ROLES.includes(r);

// iter195s — permission summary chips for higher-scope manager roles that stay
// non-togglable. hoa_manager isn't here because it now uses live per-user toggles
// (matches the board_member per-user grant model).
const rolePermsFactory = () => ({
  company_admin: ["Config editing", "Messages", "Invite team", `All ${V.lcs("org")} in company`, "Board notifications"],
  regional_manager: ["Config editing", "Messages", `Their ${V.lcs("org")}`, "Board notifications"],
});
// Multi-select community checklist
function CommunityPicker({ options, value, onChange, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  if (!options.length) return <p className="text-xs text-muted-foreground mt-1">No {V.lcs("org")} available.</p>;
  return (
    <div data-testid={testid} className="mt-1 max-h-44 overflow-y-auto rounded-xl border border-sand-200 divide-y divide-sand-100">
      {options.map((t) => (
        <button key={t.id} type="button" data-testid={`community-opt-${t.id}`} onClick={() => toggle(t.id)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-sand-50">
          <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${value.includes(t.id) ? "bg-redline-500 border-redline-500" : "border-sand-300"}`}>
            {value.includes(t.id) && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
          </span>
          <span className="truncate">{t.name}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------- Clean member row ----------------
// iter197c — `onManagePrefs` (super-admin only) opens the notification-prefs-on-behalf
// dialog so operators can turn off, e.g., a community manager's opt-in email flood
// without asking them to sign in first.
function MemberRow({ a, canManage, canManagePrefs, canLink = false, onManage, onManagePrefs, onLink }) {
  const ROLE_META = roleMetaFactory();
  const [busy, setBusy] = useState(false);
  const copyLink = async () => {
    setBusy(true);
    try {
      // user_id, not email: a mobile-only teammate has no address to look up (iter327).
      const r = await api.post("/admins/welcome-link", { user_id: a.user_id, email: a.email || "" });
      onLink?.(r.data.link);
      try {
        await navigator.clipboard.writeText(r.data.link);
        toast.success("One-tap sign-in link copied — send it to them");
      } catch {
        // iter330 — a browser can refuse a clipboard write outside a trusted gesture, and the
        // old catch reported that as "Couldn't create link". The link exists either way, so
        // surface it instead of claiming we failed to make one.
        toast.info("Sign-in link ready in the box above — copy it from there");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create link"); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admins/${a.user_id}/resend`);
      const bits = [r.data.welcomed && "email", r.data.texted && "text"].filter(Boolean);
      toast.success(bits.length ? `Invite re-sent by ${bits.join(" & ")}` : "Fresh sign-in link created (email/text not connected yet)");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend the invite"); }
    finally { setBusy(false); }
  };
  return (
    /* Mobile: identity block first, action buttons wrap onto their own row underneath.
       The old single row kept the button group `shrink-0`, which starved the name/chips
       column to zero width on a phone (name vanished, chips broke one word per line,
       buttons ran off screen). */
    <div data-testid={`admin-row-${a.user_id}`} className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 px-4 sm:px-6 py-4 border-b border-sand-100 last:border-0">
      <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
        <div className="h-9 w-9 rounded-full bg-ink text-white flex items-center justify-center font-bold shrink-0">{((a.name || a.email || a.phone || "?").trim()[0] || "?").toUpperCase()}</div>
        <div className="min-w-0 flex-1">
        <div className="font-semibold break-words">{a.name || a.email || a.phone}</div>
        {a.email
          ? <div className="text-xs text-muted-foreground break-all">{a.email}</div>
          : <div className="text-xs text-muted-foreground">Signs in by texted link — no email on file</div>}
        {a.phone && <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</div>}
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${a.role === "custom" ? "bg-redline-50 text-redline-700" : ROLE_META[a.role]?.color}`}>{a.role === "custom" ? (a.custom_role?.name || "Custom role") : (ROLE_META[a.role]?.label || a.role)}</span>
          {a.role === "custom" && a.custom_scope === "portfolio" && <span className="text-[10px] text-ink/50">· whole portfolio</span>}
          {a.role === "board_member" && a.position && <span className="text-[11px] text-ink/60">· {a.position}</span>}
          {a.invited
            ? <span title="Invite sent — this clears automatically the first time they sign in." className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">Invite pending</span>
            : <span title="They've signed in at least once." className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap"><ShieldCheck className="h-3 w-3" /> Active</span>}
          <ReachabilityBadge phoneVerified={a.phone_verified} hasPhone={!!a.phone} />
          {a.role === "board_member" && V.ownerSeat && <span title={V.ownerSeatNote} className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid="owner-full-control-chip">Full control</span>}
          {a.role === "board_member" && !V.ownerSeat && a.can_configure && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap">Can edit settings</span>}
          {a.role === "board_member" && !V.ownerSeat && a.can_broadcast && <span className="text-[10px] bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full whitespace-nowrap">Can send messages</span>}
          {/* iter195s — for hoa_managers, ONLY surface a chip when a permission is
              explicitly OFF. Default (ON) matches every other manager tier so no chip
              needed. Silent-absence-of-permission is what confused users; explicit
              "OFF" chip makes narrowed permissions visible at a glance. */}
          {a.role === "hoa_manager" && a.can_configure === false && (
            <span className="text-[10px] bg-red-50 text-red-700 px-2 py-0.5 rounded-full whitespace-nowrap">Settings editing off</span>
          )}
          {a.role === "hoa_manager" && a.can_broadcast === false && (
            <span className="text-[10px] bg-red-50 text-red-700 px-2 py-0.5 rounded-full whitespace-nowrap">Messages off</span>
          )}
          {a.board_seat?.tenant_id && (
            <span data-testid={`board-seat-${a.user_id}`} className="text-[11px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">
              {V.Grp()}{a.board_seat.position ? `: ${a.board_seat.position}` : " member"} · {a.board_seat.tenant_name || `${V.lc("org")}`}
              {a.board_seat.term_end ? (a.board_seat.term_end < new Date().toISOString().slice(0, 10) ? " · term ended" : ` · until ${a.board_seat.term_end}`) : ""}
            </span>
          )}
          {(a.role === "board_member" || a.board_seat?.tenant_id) && a.roster_person && (
            <span data-testid={`roster-link-${a.user_id}`} className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full whitespace-nowrap">
              {V.one("person")}: {a.roster_person.name}{homeLabel(a.roster_person) ? ` · ${homeLabel(a.roster_person)}` : ""}
            </span>
          )}
          {a.role === "board_member" && a.term_end && (
            <span data-testid={`term-badge-${a.user_id}`} className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${a.term_end < new Date().toISOString().slice(0, 10) ? "bg-sand-100 text-ink/50" : "bg-violet-50 text-violet-700"}`}>
              {a.term_end < new Date().toISOString().slice(0, 10) ? "Term ended" : `Term ends ${a.term_end}`}
            </span>
          )}
        </div>
        {needsCommunities(a.role) && (
          <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`admin-communities-${a.user_id}`}>
            {/* iter195u — explicit "Communities" label and clearer state for the
                Multi-Community Assignment UX. Field ask: "make it obvious how one
                HOA manager gets assigned to multiple communities" — the pill row is
                the surface, and it now labels itself + surfaces an inline
                Assign shortcut so managers don't have to hunt through the manage
                dialog to expand a manager's scope. */}
            <span className="text-[10px] uppercase tracking-wider font-bold text-ink/40 mr-1">{V.many("org")}</span>
            {(a.assignments || []).length === 0 ? (
              <span className="text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Building2 className="h-3 w-3" /> None assigned yet
              </span>
            ) : (
              (a.assignments || []).map((as, i) => (
                <span key={`${as.tenant_id}-${i}`} className="text-[11px] bg-ink/5 text-ink font-medium px-2 py-0.5 rounded-full">{as.name}</span>
              ))
            )}
            {canManage && (
              <button data-testid={`admin-assign-${a.user_id}`} onClick={() => onManage(a)}
                title={(a.assignments || []).length === 0
                  ? `Assign this manager to ${V.lcs("org")}`
                  : `Add / remove ${V.lcs("org")} · ${(a.assignments || []).length} currently assigned`}
                className="text-[11px] font-bold text-redline-600 hover:text-redline-700 hover:bg-redline-50 border border-transparent hover:border-redline-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1 transition-colors">
                <Plus className="h-3 w-3" strokeWidth={2.5} /> {(a.assignments || []).length === 0 ? "Assign" : "Add / edit"}
              </button>
            )}
          </div>
        )}
      </div>
      </div>
      {(canManage || canLink) && (
        <div className="order-3 sm:order-none w-full sm:w-auto sm:shrink-0 flex flex-wrap items-center gap-2">
          {/* iter330 — the invite tools are NOT the Manage dialog. A board member may re-send
              and copy the link for an invite they were allowed to create, while role changes,
              community assignment and removal stay with managers and above. */}
          {(canManage || canLink) && a.invited && (
            <button data-testid={`resend-invite-${a.user_id}`} onClick={resend} disabled={busy}
              title="Re-send the invite — emails a fresh sign-in link (and texts it if we have their mobile)"
              className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white text-sm font-semibold px-3 py-2 hover:bg-redline-600 transition-colors disabled:opacity-50">
              <Send className="h-4 w-4" strokeWidth={1.5} /> {busy ? "…" : "Resend"}
            </button>
          )}
          <button data-testid={`copy-link-${a.user_id}`} onClick={copyLink} disabled={busy} title="Copy one-tap sign-in link"
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink/70 text-sm font-semibold px-3 py-2 hover:border-ink hover:text-ink transition-colors disabled:opacity-50">
            <Link2 className="h-4 w-4" strokeWidth={1.5} /> {busy ? "…" : "Link"}
          </button>
          {canManagePrefs && (
            <button data-testid={`manage-prefs-${a.user_id}`} onClick={() => onManagePrefs(a)}
              title={`Manage notifications for ${a.name || a.email} — turn their emails/texts on or off on their behalf`}
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink/70 text-sm font-semibold px-3 py-2 hover:border-redline-500 hover:text-redline-600 transition-colors">
              <BellRing className="h-4 w-4" strokeWidth={1.5} /> Alerts
            </button>
          )}
          {canManage && (
            <button data-testid={`manage-member-${a.user_id}`} onClick={() => onManage(a)}
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink/70 text-sm font-semibold px-4 py-2 hover:border-ink hover:text-ink transition-colors">
              <Settings2 className="h-4 w-4" strokeWidth={1.5} /> Manage
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// iter197c — super-admin "Manage notifications for {user}" dialog. Reuses the same
// NotificationPrefs component the user sees for their own account so there's zero
// drift between the two surfaces. `targetUserId` swaps the endpoints to the
// /admin/users/{uid}/notification-prefs pair which the backend gates to super_admin only.
function ManagePrefsDialog({ member, open, onClose }) {
  if (!member) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="manage-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BellRing className="h-4 w-4 text-redline-500" /> Notifications for {member.name || member.email}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Change this team member&apos;s email and text notification preferences on their behalf. Every change is audited.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          <NotificationPrefs targetUserId={member.user_id} onDone={onClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Shared "Manage member" dialog ----------------
const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

// chips (iter392): audience chips on a team login for THIS community — what the AI may share
// with them on the phone, separate from their role (what they can do in the console).
function TeamChipsBlock({ member }) {
  const { tenantId } = useDash();
  const audiences = useAudiences(tenantId);
  const [chips, setChips] = useState(member?.audience_chips?.[tenantId] || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setChips(member?.audience_chips?.[tenantId] || []); }, [member?.user_id, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tenantId || !audiences.manual.length) return null;
  const auto = member?.role === "board_member" ? ["residents", "board"] : ["residents", "board", "staff"];
  const toggle = async (key) => {
    const next = chips.includes(key) ? chips.filter((k) => k !== key) : [...chips, key];
    setBusy(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/team/${member.user_id}/chips`, { chips: next });
      setChips(r.data.chips);
      toast.success("Audience chips updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update chips"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="team-chips-block" className="rounded-xl border border-violet-200 bg-violet-50/40 p-3.5 space-y-2">
      <div className="text-sm font-semibold text-ink">Audience chips (this {V.lc("org")})</div>
      <p className="text-xs text-muted-foreground">What the AI may share with them when they call or text. Their role already gives them: {auto.map((k) => audiences.labels[k] || k).join(", ")}{member?.role !== "board_member" ? " — managers hear everything" : ""}.</p>
      <div className="flex flex-wrap gap-1.5">
        {audiences.manual.map((a) => (
          <button key={a.key} type="button" data-testid={`team-chip-${a.key}`} disabled={busy} onClick={() => toggle(a.key)} title={a.description || a.label}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${chips.includes(a.key) ? "border-violet-600 bg-violet-600 text-white" : "border-sand-200 bg-white text-ink/60 hover:border-ink"} disabled:opacity-50`}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ManageMemberDialog({ member, open, onClose, communityOptions, companies, canChangeRole, roleOptions, onDone, canRemove = true, canGrantSeat = false }) {
  const ROLE_PERMS = rolePermsFactory();
  const ROLE_META = roleMetaFactory();
  const [role, setRole] = useState(member?.role || "");
  const [companyId, setCompanyId] = useState(member?.company_id || "");
  const [sel, setSel] = useState((member?.assignments || []).map((a) => a.tenant_id));
  const [position, setPosition] = useState(member?.position || "");
  const [termEnd, setTermEnd] = useState(member?.term_end || "");
  const [canConfig, setCanConfig] = useState(member?.can_configure ?? (member?.role === "hoa_manager"));
  const [canBroadcast, setCanBroadcast] = useState(member?.can_broadcast ?? (member?.role === "hoa_manager"));
  const [seatTenant, setSeatTenant] = useState(member?.board_seat?.tenant_id || "");
  const [seatPos, setSeatPos] = useState(member?.board_seat?.position || "");
  const [seatTerm, setSeatTerm] = useState(member?.board_seat?.term_end || "");
  const [email, setEmail] = useState(member?.email || "");
  const [phone, setPhone] = useState(member?.phone || "");
  const [linkPid, setLinkPid] = useState(member?.person_id || "");
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailClash, setEmailClash] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [sentLink, setSentLink] = useState("");
  const [signins, setSignins] = useState(null);
  const [tempPw, setTempPw] = useState("");
  const setTemporaryPassword = async () => {
    try {
      const r = await api.post(`/admins/${member.user_id}/temp-password`);
      setTempPw(r.data.temporary_password);
      toast.success("Temporary password set — read it to them; they'll be asked to change it.");
    } catch (e) { toast.error(errMsg(e, "Couldn't set a temporary password")); }
  };
  const signOutEverywhere = async () => {
    try {
      const r = await api.post(`/admins/${member.user_id}/signout-everywhere`);
      toast.success(`Signed out on ${r.data.sessions_ended} device${r.data.sessions_ended === 1 ? "" : "s"}`);
    } catch (e) { toast.error(errMsg(e, "Couldn't sign them out")); }
  };
  const loadSignins = () => {
    if (!member?.user_id) return;
    api.get(`/admins/${member.user_id}/signin-links`).then((r) => setSignins(r.data)).catch(() => setSignins(null));
  };
  useEffect(() => { if (open) loadSignins(); }, [open, member?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeIntoExisting = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admins/${member.user_id}/merge-into-existing`, {
        email: (email || "").trim(),
        tenant_id: sel[0] || member.board_seat?.tenant_id || member.tenant_id || (communityOptions[0] || {}).id || "",
        position, term_end: termEnd,
      });
      toast.success(`${V.Grp()} seat moved to ${r.data.survivor.email} — duplicate row removed`);
      setEmailClash(null); onDone(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't merge the accounts");
    } finally { setBusy(false); }
  };

  const searchResidents = async (q) => {
    setLinkQuery(q);
    if (q.trim().length < 2) { setLinkResults([]); return; }
    try {
      const r = await api.get(`/admins/${member.user_id}/resident-candidates`, { params: { q } });
      setLinkResults(r.data.results || []);
    } catch { setLinkResults([]); }
  };
  const linkResident = async (p) => {
    setLinkBusy(true);
    try {
      await api.put(`/admins/${member.user_id}/resident-link`, { person_id: p.id });
      setLinkPid(p.id); setLinkLabel(`${p.name || `${V.one("person")}`}${homeLabel(p) ? ` · ${homeLabel(p)}` : ""}`);
      setLinkResults([]); setLinkQuery(""); toast.success(`${V.one("person")} linked`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't link"); }
    finally { setLinkBusy(false); }
  };
  const unlinkResident = async () => {
    setLinkBusy(true);
    try {
      await api.put(`/admins/${member.user_id}/resident-link`, { person_id: "" });
      setLinkPid(""); setLinkLabel(""); toast.success(`${V.one("person")} unlinked`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't unlink"); }
    finally { setLinkBusy(false); }
  };

  useEffect(() => {
    setRole(member?.role || "");
    setCompanyId(member?.company_id || "");
    setSel((member?.assignments || []).map((a) => a.tenant_id));
    setPosition(member?.position || "");
    setTermEnd(member?.term_end || "");
    setCanConfig(member?.can_configure ?? (member?.role === "hoa_manager"));
    setCanBroadcast(member?.can_broadcast ?? (member?.role === "hoa_manager"));
    setSeatTenant(member?.board_seat?.tenant_id || "");
    setSeatPos(member?.board_seat?.position || "");
    setSeatTerm(member?.board_seat?.term_end || "");
    setEmail(member?.email || "");
    setPhone(member?.phone || "");
    setLinkPid(member?.person_id || "");
    setLinkQuery(""); setLinkResults([]); setLinkLabel("");
    setEmailClash(null);
    setSentLink("");
  }, [member]);

  if (!member) return null;
  const effRole = canChangeRole ? role : member.role;
  const isCompanyRole = (r) => r === "company_admin" || r === "regional_manager";
  const contactDirty = (email || "").trim().toLowerCase() !== (member.email || "").toLowerCase()
    || (phone || "").trim() !== (member.phone || "");
  const reachable = [(email || "").trim() && "email", (phone || "").trim() && "text"].filter(Boolean);

  // Contact save, on its own: sending an invite has to go to the address just typed, not the
  // stale one. Returns false when the caller should stop (an email clash needs a decision).
  const saveContact = async () => {
    if (!contactDirty) return true;
    try {
      await api.put(`/admins/${member.user_id}/contact`, { email: (email || "").trim(), phone: (phone || "").trim() });
      return true;
    } catch (err) {
      const d = err?.response?.data?.detail;
      // iter253 — "that email already signs in as X" is not a dead end: one login can hold
      // a platform role AND a board seat, so offer to move the seat there instead.
      if (err?.response?.status === 409 && d?.code === "email_in_use") {
        setEmailClash(d);
        return false;
      }
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't save their contact details");
      return false;
    }
  };

  // iter331 — invites for people ALREADY on the team, from the screen where you're looking at
  // them. The row's Resend button only appears while an invite is pending, so re-inviting an
  // existing board member or manager (or one whose email you just corrected) had nowhere to go.
  const sendInvite = async () => {
    setInviteBusy(true);
    try {
      if (!(await saveContact())) return;
      const r = await api.post(`/admins/${member.user_id}/resend`);
      const bits = [r.data.welcomed && "email", r.data.texted && "text"].filter(Boolean);
      setSentLink(r.data.invite_link || "");
      if (bits.length) toast.success(`Sign-in link sent by ${bits.join(" & ")}`);
      else toast.warning("Couldn't deliver it — copy the link below and send it to them yourself");
      loadSignins();
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't send their sign-in link");
    } finally { setInviteBusy(false); }
  };

  const copyInviteLink = async () => {
    setInviteBusy(true);
    try {
      if (!(await saveContact())) return;
      const r = await api.post("/admins/welcome-link", { user_id: member.user_id, email: (email || "").trim() });
      setSentLink(r.data.link);
      loadSignins();
      try {
        await navigator.clipboard.writeText(r.data.link);
        toast.success("One-tap sign-in link copied");
      } catch { toast.info("Sign-in link ready below — copy it from there"); }
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't create their sign-in link");
    } finally { setInviteBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (!(await saveContact())) { setBusy(false); return; }
      if (canChangeRole && role !== member.role) {
        await api.put(`/admins/${member.user_id}/role`, { role, company_id: companyId || member.company_id });
      }
      if (needsCommunities(effRole)) {
        const cur = (member.assignments || []).map((a) => a.tenant_id).slice().sort().join(",");
        if (sel.slice().sort().join(",") !== cur) {
          await api.post(`/company/assignments`, { user_id: member.user_id, tenant_ids: sel });
        }
      }
      if (effRole === "board_member" || effRole === "hoa_manager") {
        if (effRole === "board_member") {
          if ((position || "") !== (member.position || "")) await api.put(`/admins/${member.user_id}/position`, { position });
          if ((termEnd || "") !== (member.term_end || "")) await api.put(`/admins/${member.user_id}/board-term`, { term_end: termEnd });
        }
        // iter195s — hoa_manager gets the same toggles as board_member (defaulted ON).
        const curConfig = effRole === "hoa_manager"
          ? (member.can_configure === undefined ? true : !!member.can_configure)
          : !!member.can_configure;
        const curBroadcast = effRole === "hoa_manager"
          ? (member.can_broadcast === undefined ? true : !!member.can_broadcast)
          : !!member.can_broadcast;
        if (canConfig !== curConfig) await api.put(`/admins/${member.user_id}/can-configure`, { value: canConfig });
        if (canBroadcast !== curBroadcast) await api.put(`/admins/${member.user_id}/can-broadcast`, { value: canBroadcast });
      }
      if (canGrantSeat && effRole !== "board_member") {
        const cur = member.board_seat || {};
        if ((seatTenant || "") !== (cur.tenant_id || "") || (seatPos || "") !== (cur.position || "") || (seatTerm || "") !== (cur.term_end || "")) {
          await api.put(`/admins/${member.user_id}/board-seat`, { tenant_id: seatTenant, position: seatPos, term_end: seatTerm });
        }
      }
      toast.success("Member updated");
      onDone(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't save changes");
    }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${member.name || member.email} from the team?`)) return;
    try { await api.delete(`/admins/${member.user_id}`); toast.success("Member removed"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="manage-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Settings2 className="h-4 w-4 text-redline-500" /> Manage {member.name || member.email}</DialogTitle>
          <DialogDescription className="sr-only">Change this team member's role, {V.lcs("org")}, {V.grp()} position and term.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Contact & sign-in — email is their login (magic-link + Google both use it);
              mobile powers the SMS invite and their text/call alerts. */}
          <div className="rounded-xl border border-sand-200 bg-sand-50/50 p-3.5 space-y-3" data-testid="manage-contact-section">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-redline-500" /> Contact &amp; sign-in</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-ink/70 flex items-center gap-1"><Mail className="h-3 w-3" /> Email (their login)</label>
                <input data-testid="manage-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@email.com"
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              </div>
              <div>
                <label className="text-xs font-semibold text-ink/70 flex items-center gap-1"><Phone className="h-3 w-3" /> Mobile number</label>
                <input data-testid="manage-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              </div>
            </div>
            <p className="text-[11px] text-ink/45">They sign in with this email (Google or a one-tap link). Changing it invalidates old sign-in links and applies on their next sign-in. A mobile lets us text their invite and their text/call alerts.</p>

            {/* Admin-assisted mobile verification — the number won't receive text/call alerts
                until it's confirmed with a texted code. Verifying here (works great when it's the
                admin's own number on a second login) closes the "nowhere to verify" gap. */}
            {(phone || "").trim() && (
              <div data-testid="manage-phone-verify" className="pt-1">
                {member.phone_verified && (phone || "").replace(/\D/g, "") === (member.phone || "").replace(/\D/g, "") ? (
                  <div data-testid="admin-phone-verified" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1">
                    <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} /> Mobile verified — reachable for text &amp; call alerts
                  </div>
                ) : (
                  <AdminPhoneVerify userId={member.user_id} initialPhone={(phone || "").trim()} showInput={false}
                    verified={false} onVerified={() => { toast.success("Verified — closing so it's saved cleanly."); onDone(); onClose(); }} />
                )}
                <div className="mt-2" data-testid="team-member-extra-numbers">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink/50 mb-1">Additional numbers</div>
                  <ExtraNumbers endpoints={adminPhoneEndpoints(api, member.user_id)} testid={`team-phones-${member.user_id}`} showPrimary={false} who="they" onChanged={onDone} />
                </div>
              </div>
            )}
            {emailClash && (
              <div data-testid="email-clash-panel" className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2">
                <div className="text-sm font-semibold text-amber-900">{emailClash.message}</div>
                <p className="text-[11px] text-amber-900/80">
                  Two logins can&apos;t share an email — but one login can hold a platform role <em>and</em> a
                  {V.grp()} seat. Move this seat onto that account and this duplicate row goes away.
                </p>
                <div className="flex flex-wrap gap-2">
                  {emailClash.can_merge && (
                    <button data-testid="email-clash-merge" onClick={mergeIntoExisting} disabled={busy}
                      className="rounded-full bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700 transition-colors disabled:opacity-50">
                      {busy ? "Moving…" : `Move ${V.grp()} seat to ${emailClash.existing_user?.email}`}
                    </button>
                  )}
                  <button data-testid="email-clash-cancel" onClick={() => { setEmail(member.email || ""); setEmailClash(null); }}
                    className="rounded-full border border-amber-300 text-amber-900 text-xs font-semibold px-3 py-1.5 hover:bg-amber-100 transition-colors">
                    Keep the current email
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* iter331 — send the invite from HERE. Owner: "i want invites on this screen for
              existing board and managers". The row's Resend button only shows while an invite is
              pending, so an existing teammate (or one whose email you just fixed above) had no
              way to be re-invited without leaving the dialog. */}
          <div className="rounded-xl border border-sand-200 bg-white p-3.5 space-y-2.5" data-testid="manage-invite-section">
            <div className="text-sm font-semibold flex items-center gap-1.5">
              <Send className="h-3.5 w-3.5 text-redline-500" strokeWidth={1.5} />
              {member.invited && !member.last_login ? "Resend their invite" : "Send a fresh sign-in link"}
            </div>
            <p className="text-[11px] text-ink/50">
              {reachable.length
                ? <>Goes out by {reachable.join(" and ")} to {[(email || "").trim(), (phone || "").trim()].filter(Boolean).join(" · ")}. One tap, good for 72 hours, and it retires any older link.{contactDirty && <span className="text-amber-700 font-semibold"> Your edits above are saved first, so it goes to the new details.</span>}</>
                : "Add an email or a mobile number above first — there's nowhere to send it."}
            </p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="manage-send-invite" onClick={sendInvite} disabled={inviteBusy || !reachable.length}
                className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white text-sm font-semibold px-4 py-2 hover:bg-redline-600 transition-colors disabled:opacity-40">
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                {inviteBusy ? "Sending…" : (member.invited && !member.last_login ? "Resend invite" : "Send invite")}
              </button>
              <button data-testid="manage-copy-invite-link" onClick={copyInviteLink} disabled={inviteBusy}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink/70 text-sm font-semibold px-4 py-2 hover:border-ink hover:text-ink transition-colors disabled:opacity-40">
                <Link2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Copy link
              </button>
            </div>
            {sentLink && (
              <div className="flex items-center gap-2 bg-sand-50 border border-sand-200 rounded-xl px-3 py-2">
                <Link2 className="h-3.5 w-3.5 text-ink/40 shrink-0" />
                <input readOnly data-testid="manage-invite-link" value={sentLink} className="flex-1 bg-transparent text-xs outline-none" />
                <button onClick={() => { navigator.clipboard?.writeText(sentLink); toast.success("Copied"); }}
                  className="text-xs font-semibold text-redline-600 hover:text-redline-700 shrink-0">Copy</button>
              </div>
            )}
          </div>

          {/* Rescue tools — get a locked-out teammate in over the phone, no email round-trip */}
          {member?.email && (
            <div className="rounded-xl border border-sand-200 bg-white p-3.5 space-y-2" data-testid="manage-rescue">
              <div className="text-sm font-semibold flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-redline-500" strokeWidth={1.5} /> Can't sign in?</div>
              <p className="text-[11px] text-ink/50">Set a temporary password and read it to them. They sign in at askredline.com/login with their email and this password, then pick their own.</p>
              {tempPw ? (
                <div className="rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 flex items-center justify-between gap-2" data-testid="manage-temp-password">
                  <code className="font-mono text-sm font-bold text-ink">{tempPw}</code>
                  <button type="button" className="text-xs font-semibold text-redline-600" onClick={() => { navigator.clipboard?.writeText(tempPw); toast.success("Copied"); }}>Copy</button>
                </div>
              ) : (
                <button type="button" data-testid="manage-set-temp-password" onClick={setTemporaryPassword}
                  className="w-full rounded-full border border-sand-200 hover:border-ink px-4 py-2 text-sm font-semibold transition-colors">Set a temporary password</button>
              )}
              <button type="button" data-testid="manage-signout-everywhere" onClick={signOutEverywhere}
                className="w-full text-xs font-semibold text-ink/60 hover:text-danger-600 transition-colors">Sign them out on every device</button>
            </div>
          )}

          {/* Sign-in history — answers "why can't they get in?" without guessing */}
          <div className="rounded-xl border border-sand-200 bg-white p-3.5 space-y-2" data-testid="manage-signin-history">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-redline-500" strokeWidth={1.5} /> Sign-in history</div>
              <span className="text-[11px] text-ink/50">
                {signins?.last_login ? `Last signed in ${fmtWhen(signins.last_login)}` : "Never signed in"}
              </span>
            </div>
            {signins?.email && (
              <p className="text-[11px] text-ink/50">Links go to <span className="font-semibold text-ink/70">{signins.email}</span> — if they're requesting from a different address, nothing is sent.</p>
            )}
            {!signins ? (
              <p className="text-xs text-ink/40">Loading…</p>
            ) : signins.links.length === 0 ? (
              <p className="text-xs text-ink/50">No sign-in links yet.</p>
            ) : (
              <ul className="space-y-1" data-testid="manage-signin-links">
                {signins.links.slice(0, 5).map((l, i) => (
                  <li key={i} className="text-xs flex flex-wrap items-baseline gap-x-1.5 text-ink/70" data-testid={`signin-link-${i}`}>
                    <span className="font-semibold text-ink/80">{fmtWhen(l.created_at)}</span>
                    <span className="text-ink/40">·</span>
                    <span>{l.purpose === "login" ? "asked from sign-in page" : l.purpose === "password_reset" ? "set-password link" : l.purpose === "board_welcome" ? `${V.grp()} welcome` : "invite from admin"}</span>
                    <span className="text-ink/40">·</span>
                    {l.revoked_reason === "superseded" ? (
                      <span className="text-amber-700">replaced by a newer link</span>
                    ) : l.revoked_reason === "password_set" ? (
                      <span className="text-emerald-700">used to set a password {fmtWhen(l.first_opened_at)}</span>
                    ) : l.used ? (
                      <span className="text-emerald-700">opened {fmtWhen(l.first_opened_at)} by {l.opened_by}{l.redeem_count > 1 ? ` (${l.redeem_count}×)` : ""}</span>
                    ) : (l.expires_at && l.expires_at < new Date().toISOString()) ? (
                      <span className="text-ink/50">expired unopened</span>
                    ) : (
                      <span className="text-sky-700">not opened yet</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Resident profile link (admin, trusted — no code) */}
          <div className="rounded-xl border border-sand-200 bg-sand-50/50 p-3.5 space-y-2" data-testid="manage-resident-link">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Home className="h-3.5 w-3.5 text-redline-500" /> {V.one("person")} profile</div>
            {linkPid ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1"><ShieldCheck className="h-3 w-3" /> Linked{linkLabel ? `: ${linkLabel}` : ""}</span>
                <button data-testid="manage-resident-unlink" onClick={unlinkResident} disabled={linkBusy} className="text-xs text-ink/50 hover:text-ink inline-flex items-center gap-1"><Unlink className="h-3 w-3" /> Unlink</button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2 rounded-xl border border-sand-200 px-3 py-2 bg-white">
                  <Search className="h-3.5 w-3.5 text-ink/40 shrink-0" />
                  <input data-testid="manage-resident-search" value={linkQuery} onChange={(e) => searchResidents(e.target.value)}
                    placeholder={`Search this ${V.lc("org")}'s roster by name, email or phone`}
                    className="flex-1 text-sm outline-none bg-transparent" />
                </div>
                {linkResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-xl border border-sand-200 bg-white shadow-lg max-h-52 overflow-y-auto">
                    {linkResults.map((p) => (
                      <button key={p.id} data-testid={`resident-candidate-${p.id}`} onClick={() => linkResident(p)}
                        className="w-full text-left px-3 py-2 hover:bg-sand-50 border-b border-sand-100 last:border-0">
                        <div className="text-sm font-semibold text-ink truncate">{p.name || p.email || `${V.one("person")}`}</div>
                        <div className="text-xs text-muted-foreground truncate">{[homeLabel(p), p.email, p.phone].filter(Boolean).join(" · ")}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-[11px] text-ink/45">Link this member to their property in the roster so their {V.grp()} + {V.lc("person")} roles share one profile.</p>
          </div>

          {/* Role */}
          <div>
            <label className="text-sm font-semibold">Role</label>
            {canChangeRole ? (
              <select data-testid="manage-role" value={role} onChange={(e) => setRole(e.target.value)}
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                {roleOptions.map((r) => <option key={r} value={r}>{ROLE_META[r]?.label || r}</option>)}
              </select>
            ) : (
              <div className="mt-1"><span className={`text-xs px-2.5 py-1 rounded-full ${ROLE_META[member.role]?.color}`}>{ROLE_META[member.role]?.label}</span></div>
            )}
          </div>

          {/* Company (for company-scoped roles when changing role) */}
          {canChangeRole && isCompanyRole(effRole) && (companies || []).length > 0 && (
            <div>
              <label className="text-sm font-semibold">Management company</label>
              <select data-testid="manage-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                <option value="">Select a company…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          {/* Communities */}
          {needsCommunities(effRole) && (
            <div>
              <label className="text-sm font-semibold flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Assigned {V.lcs("org")} <span className="text-xs font-normal text-muted-foreground">— {effRole === "hoa_manager" ? `select every ${V.lc("org")} this manager runs` : effRole === "regional_manager" ? `select the ${V.lcs("org")} this manager runs (leave empty for all in the company)` : `select which ${V.lcs("org")} this ${V.grp()} seat covers`}</span></label>
              <CommunityPicker testid="manage-communities" options={communityOptions} value={sel} onChange={setSel} />
              {effRole === "hoa_manager" && (
                <p className="text-[11px] text-muted-foreground mt-1.5">One {V.lc("staff")} can run several {V.lcs("org")} — check every one they manage. The concierge, inbox, and dashboards will scope to just those.</p>
              )}
            </div>
          )}

          {/* iter195s — Read-only permission summary for HIGHER manager roles
              (regional_manager, company_admin) whose permissions are still baked
              in and can't be narrowed at the community level. hoa_manager now gets
              real toggles below (matches the board_member pattern). */}
          {effRole && ["company_admin", "regional_manager"].includes(effRole) && (
            <div data-testid="manage-role-perms" className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3.5">
              <div className="text-sm font-semibold text-emerald-900 mb-2">Permissions for {ROLE_META[effRole]?.label || effRole.replace(/_/g, " ")}</div>
              <div className="flex flex-wrap gap-1.5">
                {ROLE_PERMS[effRole].map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-200 text-emerald-800 text-[11px] font-semibold px-2 py-0.5">
                    <Check className="h-3 w-3" /> {p}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-emerald-700/80 mt-2">These are built into the role. To narrow, invite them as {V.a("staff")} with the specific toggles you want, or reduce their {V.lc("org")} assignments.</p>
            </div>
          )}

          {/* iter195s — HOA manager permission toggles (defaulted ON; unset field on
              existing managers is treated as ON so nothing breaks). Higher-scope
              admins can flip them off to narrow a manager who shouldn't blast or edit
              settings for a specific community. */}
          {effRole === "hoa_manager" && (
            <div data-testid="manager-permissions" className="rounded-xl border border-sand-200 bg-sand-50/40 p-3.5 space-y-3">
              <div className="text-sm font-semibold text-ink">Permissions for this manager</div>
              <label className="flex items-center gap-3 cursor-pointer">
                <button type="button" data-testid="manage-can-config" onClick={() => setCanConfig((v) => !v)}
                  className={`h-6 w-11 shrink-0 rounded-full transition-colors relative ${canConfig ? "bg-emerald-500" : "bg-sand-300"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${canConfig ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <span className="text-sm"><span className="font-semibold">Allow config editing</span><span className="block text-xs text-muted-foreground">Edit the {V.lc("org")}'s assistant settings, quick facts, roster, and modules</span></span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <button type="button" data-testid="manage-can-broadcast" onClick={() => setCanBroadcast((v) => !v)}
                  className={`h-6 w-11 shrink-0 rounded-full transition-colors relative ${canBroadcast ? "bg-emerald-500" : "bg-sand-300"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${canBroadcast ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <span className="text-sm"><span className="font-semibold">Allow messages</span><span className="block text-xs text-muted-foreground">Text or email {V.lcs("person")} from the Messages page (and via Copilot)</span></span>
              </label>
            </div>
          )}

          {(effRole === "hoa_manager" || effRole === "board_member") && <TeamChipsBlock member={member} />}

          {/* Board-only: position + config access. Where the board seat runs the business (a roofing
              company's Owner — V.ownerSeat), the read-only toggles make no sense: one plain note instead. */}
          {effRole === "board_member" && V.ownerSeat && (
            <>
              {member.roster_person && (
                <div data-testid="manage-roster-link" className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800">
                  Linked to roster {V.lc("person")}: <span className="font-semibold">{member.roster_person.name}</span>{member.roster_person.unit ? ` · ${member.roster_person.unit}` : ""} — one identity: the concierge recognizes them as both {V.lc("person")} and {V.lc("board")}.
                </div>
              )}
              <div data-testid="owner-full-control-note" className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3.5">
                <div className="text-sm font-semibold text-emerald-900 inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> {V.many("board")} have full control of this {V.lc("org")}</div>
                <p className="text-xs text-emerald-800/80 mt-1">{V.ownerSeatNote || `Everything ${V.a("staff")} can do — documents, settings the dials allow, requests, messages, inviting the team — under the ${V.one("board")} title. Nothing to switch on.`}</p>
              </div>
            </>
          )}
          {effRole === "board_member" && !V.ownerSeat && (
            <>
              {member.roster_person && (
                <div data-testid="manage-roster-link" className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800">
                  Linked to roster {V.lc("person")}: <span className="font-semibold">{member.roster_person.name}</span>{homeLabel(member.roster_person) ? ` · ${homeLabel(member.roster_person)}` : ""} — one identity: the concierge recognizes them as both {V.lc("person")} and {V.lc("board")}.
                </div>
              )}
              <div>
                <label className="text-sm font-semibold">{V.Grp()} position</label>
                <input data-testid="manage-position" value={position} onChange={(e) => setPosition(e.target.value)}
                  placeholder="President, Treasurer…" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
              </div>
              <div>
                <label className="text-sm font-semibold">Term ends <span className="font-normal text-muted-foreground">(optional)</span></label>
                <input type="date" data-testid="manage-term-end" value={termEnd} onChange={(e) => setTermEnd(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
                <p className="text-xs text-muted-foreground mt-1">After this date the AI concierge automatically treats them as a regular {V.lc("person")} again — the elevated greeting and {V.grp()} access expire on their own. Leave blank for no end date.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <button type="button" data-testid="manage-can-config" onClick={() => setCanConfig((v) => !v)}
                  className={`h-6 w-11 shrink-0 rounded-full transition-colors relative ${canConfig ? "bg-emerald-500" : "bg-sand-300"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${canConfig ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <span className="text-sm"><span className="font-semibold">Allow config editing</span><span className="block text-xs text-muted-foreground">{V.many("board")} are read-only by default — flip this ON to let them edit their {V.lc("org")}'s assistant settings (a permission managers already have)</span></span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <button type="button" data-testid="manage-can-broadcast" onClick={() => setCanBroadcast((v) => !v)}
                  className={`h-6 w-11 shrink-0 rounded-full transition-colors relative ${canBroadcast ? "bg-emerald-500" : "bg-sand-300"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${canBroadcast ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <span className="text-sm"><span className="font-semibold">Allow broadcasts</span><span className="block text-xs text-muted-foreground">Flip ON to let this {V.lc("board")} send announcements & alerts to {V.lcs("person")} (a permission managers already have)</span></span>
              </label>
            </>
          )}

          {/* Staff board seat: any staff login can ALSO sit on one community's board */}
          {canGrantSeat && effRole !== "board_member" && (
            <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-3.5 space-y-3" data-testid="board-seat-section">
              <div>
                <div className="text-sm font-semibold text-violet-900">{V.Grp()} seat <span className="font-normal text-violet-700/70">(optional)</span></div>
                <p className="text-xs text-violet-800/70 mt-0.5">Staff can also sit on {V.a("org")}'s {V.grp()}. The concierge will greet and treat them as that {V.lc("board")} on calls & texts — their platform role stays unchanged, and it expires automatically with the term.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-violet-900">{V.one("org")}</label>
                <select data-testid="seat-tenant" value={seatTenant} onChange={(e) => setSeatTenant(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                  <option value="">No {V.grp()} seat</option>
                  {communityOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {seatTenant && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-violet-900">Position</label>
                    <input data-testid="seat-position" value={seatPos} onChange={(e) => setSeatPos(e.target.value)}
                      placeholder="President, Treasurer…" className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-violet-900">Term ends</label>
                    <input type="date" data-testid="seat-term" value={seatTerm} onChange={(e) => setSeatTerm(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-sand-100">
            {canRemove ? (
              <button data-testid="manage-remove" onClick={remove} className="inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600 hover:text-redline-700">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Remove
              </button>
            ) : <span />}
            <button data-testid="manage-save" disabled={busy} onClick={save}
              className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Company (Administrator) team management ----------------
function CompanyTeam({ companyId = null, readOnly: readOnlyProp = false }) {
  const ROLE_META = roleMetaFactory();
  // `me` is the signed-in user_id (a string); `meUser` is the whole record. Reading a role off
  // `me` silently yields undefined — that bug downgraded a super admin to an Administrator here.
  const { me, meUser, actualRole } = useDash();
  const [data, setData] = useCachedState(`team-co:${companyId || "mine"}`, { members: [], communities: [] });
  const [form, setForm] = useState({ email: "", name: "", phone: "", role: "regional_manager", tenant_ids: [] });
  const [link, setLink] = useState("");
  const [match, setMatch] = useState({ link_roster: true, change_role: false });   // iter475e — roster link / re-seat confirmations
  const [clash, setClash] = useState(null);
  const [managing, setManaging] = useState(null);
  const [managingPrefs, setManagingPrefs] = useState(null);
  // iter432 — a regional manager gets this page read-only: who runs the communities they run.
  const readOnly = readOnlyProp || !!data.read_only;

  const cq = companyId ? `?company_id=${companyId}` : "";
  const [customRoles, setCustomRoles] = useState([]);
  const load = useCallback(() => api.get(`/company/team${cq}`).then((r) => setData(r.data)), [cq]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!readOnlyProp) api.get(`/company/roles${cq}`).then((r) => setCustomRoles(r.data.roles || [])).catch(() => {}); }, [cq, readOnlyProp]);

  const communities = data.communities || [];
  const isCustom = form.role === "custom";
  const customNeedsCommunities = isCustom && form.custom_scope !== "portfolio";

  const invite = async () => {
    // iter327 — either contact will do. A mobile-only teammate signs in from the texted link.
    if (!form.email.trim() && !form.phone.trim()) { toast.error("Add an email address or a mobile number"); return; }
    if (form.email.trim() && !form.email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!form.email.trim() && form.phone.replace(/\D/g, "").length < 10) { toast.error("Enter a full 10-digit mobile number"); return; }
    if (isCustom && !form.custom_role_id) { toast.error("Pick a role"); return; }
    // A regional manager can be added before their communities are decided.
    if (needsCommunities(form.role) && form.role !== "regional_manager" && form.tenant_ids.length === 0) {
      toast.error(`Select at least one ${V.lc("org")}`); return;
    }
    if (customNeedsCommunities && form.tenant_ids.length === 0) { toast.error(`Select at least one ${V.lc("org")}, or give them the whole portfolio`); return; }
    try {
      const payload = { email: form.email, name: form.name, phone: form.phone, role: form.role, tenant_ids: form.tenant_ids,
                        link_roster: match.link_roster !== false, change_role: !!match.change_role };
      if (companyId) payload.company_id = companyId;
      if (isCustom) { payload.custom_role_id = form.custom_role_id; payload.custom_scope = form.custom_scope || "communities"; }
      const r = await api.post("/admins/invite", payload);
      const bits = [];
      if (r.data.welcomed) bits.push("welcome email sent");
      if (r.data.texted) bits.push("sign-in link texted");
      if (r.data.roster?.linked) bits.push(`linked to their ${V.lc("person")} record${r.data.roster.tenant_name ? ` at ${r.data.roster.tenant_name}` : ""}`);
      if (!bits.length) bits.push("copy the link below and send it to them");
      const roleLabel = r.data.role === "custom" ? (customRoles.find((c) => c.id === form.custom_role_id)?.name || "custom role") : ROLE_META[r.data.role].label;
      toast.success(`${r.data.email || r.data.phone} invited as ${roleLabel} · ${bits.join(" · ")}`);
      setLink(r.data.invite_link);
      setForm({ email: "", name: "", phone: "", role: "regional_manager", tenant_ids: [] });
      setMatch({ link_roster: true, change_role: false }); setClash(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "email_in_use") { setClash(d); toast.error(d.message); return; }
      toast.error(errMsg(e, "Failed"));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid={readOnly ? "company-team-readonly" : "company-team"}>
      <div>
        <PageTitle title={V.mod("team")} />
        {readOnly ? (
          <p className="text-muted-foreground mt-1" data-testid="company-team-readonly-note">Who runs the {V.lcs("org")} you manage — the firm&apos;s {V.role("company_admin").toLowerCase()}s, fellow {V.role("regional_manager").toLowerCase()}s and the {V.role("hoa_manager").toLowerCase()}s assigned there. {V.role("company_admin")}s invite and assign people.</p>
        ) : (
          <p className="text-muted-foreground mt-1">Invite another {V.role("company_admin").toLowerCase()} for the whole portfolio, or a {V.role("regional_manager").toLowerCase()} and assign the {V.lcs("org")} they run. They sign in with Google using the invited email.</p>
        )}
      </div>

      {!readOnly && <div className="bg-white rounded-2xl border border-sand-200 p-6">
        <div className="flex items-center gap-2 font-heading font-bold mb-4"><UserPlus className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> Invite a team member</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold">Email <span className="font-normal text-muted-foreground">(or use a mobile below)</span></label>
            <input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="manager@example.com" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div>
            <label className="text-sm font-semibold">Name (optional)</label>
            <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div>
            <label className="text-sm font-semibold">Mobile number <span className="font-normal text-muted-foreground">(we&apos;ll text the sign-in link)</span></label>
            <input data-testid="invite-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 512 555 0100" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div>
            <label className="text-sm font-semibold">Role</label>
            <select data-testid="invite-role" value={isCustom ? `custom:${form.custom_role_id}` : form.role} onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith("custom:")) setForm({ ...form, role: "custom", custom_role_id: v.slice(7), custom_scope: form.custom_scope || "communities" });
                else setForm({ ...form, role: v, custom_role_id: undefined, custom_scope: undefined });
              }}
              className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
              {/* Administrators seat another Administrator (whole portfolio) or a Community Manager
                  (a set of communities). Board members are community-level and can only be
                  invited from the community's own Team page. Custom roles are defined on the
                  Roles page. */}
              {["regional_manager", "company_admin"].map((r) => <option key={r} value={r}>{r === "company_admin" ? `${V.one("org")} ${ROLE_META[r].label} — whole portfolio` : `${ROLE_META[r].label} — their ${V.lcs("org")}`}</option>)}
              {customRoles.length > 0 && (
                <optgroup label="Your custom roles">
                  {customRoles.map((c) => <option key={c.id} value={`custom:${c.id}`}>{c.name}</option>)}
                </optgroup>
              )}
            </select>
            {!isCustom && (
              <p className="mt-1.5 text-xs text-muted-foreground" data-testid="invite-role-note">
                {form.role === "company_admin"
                  ? `Every ${V.lc("org")} in the portfolio, plus billing, add-ons and inviting people.`
                  : `Runs the ${V.lcs("org")} you pick below — config editing, messages and board notifications. Leave the list empty to cover all of them.`}
              </p>
            )}
          </div>
          {isCustom && (
            <div data-testid="invite-custom-scope">
              <label className="text-sm font-semibold">Scope</label>
              <div className="mt-1 inline-flex rounded-full border border-sand-200 bg-white p-0.5 w-full">
                {[["communities", `Specific ${V.lcs("org")}`], ["portfolio", "Whole portfolio"]].map(([val, lbl]) => (
                  <button key={val} type="button" data-testid={`invite-scope-${val}`} onClick={() => setForm({ ...form, custom_scope: val })}
                    className={`flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${(form.custom_scope || "communities") === val ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>{lbl}</button>
                ))}
              </div>
            </div>
          )}
          {(needsCommunities(form.role) || customNeedsCommunities) && (
            <div>
              <label className="text-sm font-semibold">{V.many("org")} {form.role === "regional_manager" && <span className="text-muted-foreground font-normal">(optional)</span>}</label>
              <CommunityPicker testid="invite-communities" options={communities} value={form.tenant_ids} onChange={(v) => setForm({ ...form, tenant_ids: v })} />
            </div>
          )}
        </div>
        <InviteMatchPanel email={form.email} phone={form.phone} role={form.role} companyId={companyId || meUser?.company_id || ""} tenantIds={form.tenant_ids}
          roleLabel={isCustom ? (customRoles.find((c) => c.id === form.custom_role_id)?.name || "that role") : ROLE_META[form.role]?.label}
          clash={clash} value={match} onChange={(v) => { setMatch(v); if (v.change_role) setClash(null); }} />
        <button data-testid="invite-submit" onClick={invite} className="mt-5 rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors">Send invite</button>
        {link && (
          <div className="mt-4">
            <div className="text-xs font-semibold text-ink mb-1.5">One-tap sign-in link — send this and they&apos;re in (works even if the email doesn&apos;t arrive):</div>
            <div className="flex items-center gap-2 bg-sand-50 border border-sand-200 rounded-xl p-3">
              <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <input readOnly value={link} className="flex-1 bg-transparent text-sm outline-none" data-testid="invite-link-value" />
              <button data-testid="invite-link-copy" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="flex items-center gap-1 text-sm font-semibold text-redline-600 hover:text-redline-700"><Copy className="h-3.5 w-3.5" /> Copy</button>
            </div>
          </div>
        )}
      </div>}

      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
        <div className="flex items-center gap-2 font-heading font-bold px-6 py-4 border-b border-sand-200"><Users2 className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> Team</div>
        {data.members.map((a) => {
          // iter198o — who can manage this row?
          //   • Nobody manages themselves (self-lock guards against accidental self-remove).
          //   • Super admins can manage EVERYONE, including other super admins and Administrators
          //     — they own the platform and are the only escape hatch to remove a seed test
          //     account or a stale Administrator. Field bug: prior check hid the Manage button
          //     for every company_admin row, so super admins had no UI path to remove a
          //     seed Test Company Admin (`company@redline.test`) from a portfolio.
          //   • Administrators (company_admin viewers) manage their team but NOT other Administrators
          //     — peer protection (backend also enforces via `revoke_admin`).
          // iter530 — `me` is the user_id STRING and `me?.role` was always undefined, so a super
          // admin viewing a portfolio read as "not super": every Administrator row lost Manage,
          // which is why a portfolio could not be emptied (and therefore not deleted).
          const isSelf = a.user_id === me;
          const viewerIsSuper = (actualRole || meUser?.role) === "super_admin";
          const isPeerCompanyAdmin = a.role === "company_admin" && !viewerIsSuper;
          const canManage = !readOnly && !isSelf && !isPeerCompanyAdmin;
          return (
            <MemberRow key={a.user_id} a={a} canManage={canManage}
              canManagePrefs={false} onManage={setManaging} onManagePrefs={setManagingPrefs} onLink={setLink} />
          );
        })}
        {data.members.length === 0 && <div className="px-6 py-8 text-sm text-muted-foreground text-center">No team members yet.</div>}
      </div>

      <ManageMemberDialog member={managing} open={!!managing} onClose={() => setManaging(null)}
        communityOptions={communities} companies={[]} canChangeRole={false} roleOptions={[]} onDone={load} />
      <ManagePrefsDialog member={managingPrefs} open={!!managingPrefs} onClose={() => setManagingPrefs(null)} />
    </div>
  );
}

// ---------------- Super-admin / HOA-manager team (Redline staff view) ----------------
function StaffTeam({ role, tenants, tenantId }) {
  const ROLE_META = roleMetaFactory();
  const { me } = useDash();   // the user_id string
  const v = useVocab();
  const [admins, setAdmins] = useCachedState(`admins:${tenantId || "all"}`, []);
  const [companies, setCompanies] = useCachedState("companies", []);
  const [form, setForm] = useState({ email: "", name: "", phone: "", role: "hoa_manager", company_id: "", tenant_ids: [], tenant_id: tenantId, lives_here: false, unit: "" });
  const [link, setLink] = useState("");
  const [match, setMatch] = useState({ link_roster: true, change_role: false });   // iter475e — roster link / re-seat confirmations
  const [clash, setClash] = useState(null);
  const [managing, setManaging] = useState(null);
  const [managingPrefs, setManagingPrefs] = useState(null);

  const isSuper = role === "super_admin";
  // Inside a community, EVERYONE (super admins included) sees only that community's team —
  // its managers and board. The global staff roster lives outside community mode.
  const scoped = !!tenantId;
  const tName = tenants.find((t) => t.id === tenantId)?.name || "";
  const load = useCallback(() => api.get(`/admins${scoped ? `?tenant_id=${tenantId}` : ""}`).then((r) => setAdmins(r.data)), [scoped, tenantId]);
  useEffect(() => { load(); if (isSuper && !scoped) api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, [load, isSuper, scoped]);

  const roleOptions = scoped
    ? ["hoa_manager", "board_member"]
    : isSuper
      // iter198q — global Redline staff invite: platform tier only. Regional managers
      // and HOA community managers live inside a portfolio, board members live inside
      // a community — invite each from the appropriate drill-in surface. Prevents a
      // super admin from accidentally creating a portfolio-scoped role without a
      // portfolio context, and keeps the display matched to the hierarchy the user
      // asked for ("board members and community managers appear in super admin team
      // area — the hierarchy is all messed up").
      ? ["operator", "platform_admin", "onboarding_admin", "company_admin", "super_admin"]
      : ["hoa_manager", "board_member"];
  const isCompanyRole = (r) => r === "company_admin" || r === "regional_manager";
  const companyTenants = form.company_id ? tenants.filter((t) => t.company_id === form.company_id) : tenants;

  // iter330 — the invite tools (Resend + copy the one-tap Link) are separate from the Manage
  // dialog, which owns role changes, community assignment and removal. A manager may re-send to
  // any manager or board member here; a board member may re-send an invite they were allowed to
  // create, and the backend limits that to one still PENDING, so a live peer can't be taken over.
  const canUseLinkTools = (a) => {
    if (a.user_id === me) return false;
    if (!["hoa_manager", "board_member"].includes(a.role)) return false;
    if (role === "board_member") return !!a.invited && !a.last_login;
    return ["hoa_manager", "company_admin", "regional_manager"].includes(role);
  };

  const invite = async () => {
    // iter330 — this is the page a manager or board member actually uses, and it still
    // hard-required an email, so the phone-only invite added in iter327 was unreachable here.
    if (!form.email.trim() && !form.phone.trim()) { toast.error("Add an email address or a mobile number"); return; }
    if (form.email.trim() && !form.email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!form.email.trim() && form.phone.replace(/\D/g, "").length < 10) { toast.error("Enter a full 10-digit mobile number"); return; }
    if (form.lives_here && !form.unit.trim()) { toast.error("Add their unit or address so we can file their roster record"); return; }
    if (!scoped && isCompanyRole(form.role) && !form.company_id) { toast.error("Select a management company"); return; }
    try {
      const payload = { email: form.email, name: form.name, phone: form.phone, role: form.role,
                        lives_here: !!form.lives_here, unit: form.unit || "",
                        link_roster: match.link_roster !== false, change_role: !!match.change_role };
      if (isCompanyRole(form.role)) payload.company_id = form.company_id;
      if (needsCommunities(form.role)) {
        payload.tenant_ids = scoped ? [tenantId] : form.tenant_ids;
        if (form.role !== "regional_manager" && payload.tenant_ids.length === 0) { toast.error(`Select at least one ${V.lc("org")}`); return; }
      }
      const r = await api.post("/admins/invite", payload);
      const bits = [];
      if (r.data.welcomed) bits.push("welcome email sent");
      if (r.data.texted) bits.push("sign-in link texted");
      if (r.data.roster?.created) bits.push("added to the roster");
      else if (r.data.roster?.linked) bits.push(`linked to their ${V.lc("person")} record`);
      if (!bits.length) bits.push("copy the link below and send it to them");
      toast.success(`${r.data.email || r.data.phone} invited as ${ROLE_META[r.data.role].label} · ${bits.join(" · ")}`);
      if (r.data.roster?.note) toast.info(r.data.roster.note);
      setLink(r.data.invite_link);
      setForm({ email: "", name: "", phone: "", role: "hoa_manager", company_id: "", tenant_ids: [], tenant_id: tenantId, lives_here: false, unit: "" });
      setMatch({ link_roster: true, change_role: false }); setClash(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "email_in_use") { setClash(d); toast.error(d.message); return; }
      toast.error(errMsg(e, "Failed"));
    }
  };

  // For the manage dialog: which communities can be assigned to this member
  const manageCommunities = (m) => {
    if (scoped) return [{ id: tenantId, name: tName }];
    const base = (m?.company_id ? tenants.filter((t) => t.company_id === m.company_id) : tenants);
    return base.map((t) => ({ id: t.id, name: t.name }));
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <PageTitle title={<>{scoped ? `${v.mod("team")} — ${tName}` : v.mod("team")}</>} about={<>{scoped
            ? `Everyone with access to ${tName} — its managers and ${V.lcs("board")}. Invites from here are for this ${V.lc("org")} only.`
            : `Invite Redline operators, platform admins, and management-company Administrators. To add a ${V.one("staff")}, drill into a portfolio first (View as admin). To add ${V.a("board")}, drill into a specific ${V.lc("org")}'s Team page.`}</>} />
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 p-6">
        <div className="mb-4">
          <div className="flex items-center gap-2 font-heading font-bold"><UserPlus className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> {scoped ? `Invite to the ${tName} admin team` : "Invite an admin"}</div>
          {scoped && <p className="text-xs text-muted-foreground mt-1">{V.many("board")} and property managers who help run {tName}. An email or a mobile number is enough — we send a one-tap sign-in link to whichever you give us.</p>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold">Email <span className="font-normal text-muted-foreground">(or use a mobile below)</span></label>
            <input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="manager@example.com" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div>
            <label className="text-sm font-semibold">Name (optional)</label>
            <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div>
            <label className="text-sm font-semibold">Mobile number <span className="font-normal text-muted-foreground">(or use this on its own — we&apos;ll text the link)</span></label>
            <input data-testid="invite-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 512 555 0100" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div>
            <label className="text-sm font-semibold">Role</label>
            <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, tenant_ids: [] })}
              className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
              {roleOptions.map((r) => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
            </select>
          </div>
          {!scoped && isSuper && isCompanyRole(form.role) && (
            <div>
              <label className="text-sm font-semibold">Management company</label>
              <select data-testid="invite-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, tenant_ids: [] })}
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                <option value="">Select a company…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {needsCommunities(form.role) && !scoped && isSuper && (
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold">{V.many("org")} {form.role === "regional_manager" && <span className="text-muted-foreground font-normal">(optional)</span>}</label>
              <CommunityPicker testid="invite-communities" options={companyTenants} value={form.tenant_ids} onChange={(v) => setForm({ ...form, tenant_ids: v })} />
            </div>
          )}
          {needsCommunities(form.role) && scoped && (
            <div>
              <label className="text-sm font-semibold">{V.one("org")}</label>
              <input readOnly data-testid="invite-community-fixed" value={tName} className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-sand-50 outline-none" />
            </div>
          )}
        </div>
        <InviteMatchPanel email={form.email} phone={form.phone} role={form.role} companyId={isCompanyRole(form.role) ? form.company_id : (tenants.find((t) => t.id === tenantId)?.company_id || "")}
          tenantIds={needsCommunities(form.role) ? (scoped ? [tenantId] : form.tenant_ids) : []} roleLabel={ROLE_META[form.role]?.label}
          clash={clash} value={match} onChange={(v) => { setMatch(v); if (v.change_role) setClash(null); }} />
        <button data-testid="invite-submit" onClick={invite} className="mt-5 rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors">Send invite</button>
        {link && (
          <div className="mt-4 flex items-center gap-2 bg-sand-50 border border-sand-200 rounded-xl p-3">
            <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <input readOnly value={link} className="flex-1 bg-transparent text-sm outline-none" />
            <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Invite link copied"); }} className="flex items-center gap-1 text-sm font-semibold text-redline-600 hover:text-redline-700"><Copy className="h-3.5 w-3.5" /> Copy</button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">A one-tap sign-in link goes out by email, and by text if you add a mobile. Mobile only is fine — the texted link is all they need, and they can add an email later from their Account page to sign in with Google. The link above is a backup you can share any way you like.</p>
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
        <div className="flex items-center gap-2 font-heading font-bold px-6 py-4 border-b border-sand-200"><Users2 className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> {scoped ? `${tName} admin team` : "Admins"}</div>
        {admins.length === 0 && <div className="px-6 py-8 text-sm text-muted-foreground text-center">No team members yet — invite the first one above.</div>}
        {admins.map((a) => (
          <MemberRow key={a.user_id} a={a}
            /* iter198o — the self-lock check compares user_ids, not user_id vs the whole
               `me` object. Before iter198o these comparisons compared a string to an
               object, which was ALWAYS truthy — so the self-lock never actually engaged
               (super admins were seeing manage buttons on their own row) and other
               conditions carried the check. Fixed here + in CompanyTeam. */
            canManage={isSuper
              || (role === "hoa_manager" && a.role === "board_member" && a.user_id !== me)
              || (scoped && (role === "company_admin" || role === "regional_manager")
                  && ["hoa_manager", "board_member"].includes(a.role) && a.user_id !== me)}
            canManagePrefs={isSuper && a.user_id !== me
              && ["hoa_manager", "board_member", "company_admin", "regional_manager"].includes(a.role)}
            canLink={canUseLinkTools(a)}
            onManage={setManaging} onManagePrefs={setManagingPrefs} onLink={setLink} />
        ))}
      </div>

      <ManageMemberDialog member={managing} open={!!managing} onClose={() => setManaging(null)}
        communityOptions={managing ? manageCommunities(managing) : []} companies={companies}
        canChangeRole={isSuper && managing?.user_id !== me} canRemove={isSuper && managing?.user_id !== me}
        canGrantSeat={isSuper && !scoped} roleOptions={roleOptions} onDone={load} />
      <ManagePrefsDialog member={managingPrefs} open={!!managingPrefs} onClose={() => setManagingPrefs(null)} />
    </div>
  );
}

export default function Team() {
  const { role, actualRole, tenants, tenantId, viewCompanyId } = useDash();
  // Community mode: EVERYONE — super admins, administrators and regionals included — sees only
  // this community's team, and invites from here are locked to this community.
  if (tenantId) return <StaffTeam role={role} tenants={tenants} tenantId={tenantId} />;
  if (actualRole === "company_admin") return <CompanyTeam />;
  if (actualRole === "regional_manager") return <CompanyTeam readOnly />;
  if (actualRole === "super_admin" && viewCompanyId) return <CompanyTeam companyId={viewCompanyId} />;
  return <StaffTeam role={role} tenants={tenants} tenantId={tenantId} />;
}
